/**
 * SignalRService
 *
 * Purpose
 * - Provides a single, reliable SignalR 2.x client for the Angular SPA.
 * - Loads generated proxies from /signalr/hubs and wires client handlers for the priceManagement hub.
 * - Ensures a single start path via ensureStarted() to prevent double-start races and proxy/hub mismatches.
 * - Adds robust diagnostics, request correlation, and timeout/retry behavior for hub invocations.
 *
 * Usage
 * - Initialization:
 *   - Call loadSignalRHubs() once after auth; it pins window.jQuery to the local jQuery, loads /signalr/hubs, then calls initSignalR().
 *   - All hub method calls are gated on ensureStarted(); do not call $.connection.hub.start() directly.
 * - Invocations:
 *   - Use the server proxy through invokeWithReconnectOnce(...) for consistent retry/timeout/telemetry.
 *     Example:
 *       const call = () => this.ensureHubProxy().server.workInStore(storeId);
 *       this.invokeWithReconnectOnce(call, 30000, 'workInStore').then(...).catch(...);
 * - Monitoring:
 *   - Subscribe to connectionState$ to react to 'connecting' | 'connected' | 'reconnecting' | 'disconnected'.
 *   - “Slow-call” hint: operations that exceed 10 seconds log [SignalR][telemetry] invoke_slow. Timeouts show a user-friendly error and log invoke_timeout.
 *
 * Changes since previous implementation (from “Compare with Unmodified”)
 * - Startup and lifecycle
 *   - Removed signalRInitialized flag; all hub calls now go through ensureStarted().
 *   - Centralized base URL resolution via resolveBaseUrl(); uses AppConfig.settings.baseUrl, falling back to window origin.
 *   - Keeps proxies/jQuery in sync: sets window.$/window.jQuery before loading /signalr/hubs so generated proxies bind the same jQuery.
 *   - Skips reloading /signalr/hubs if proxies already exist; forces a fresh start when proxies are reloaded.
 *   - Gracefully stops the hub on window beforeunload to avoid zombie connections on the server.
 * - Authentication and correlation
 *   - Continues attaching access_token via hub.qs for all transports.
 *   - Adds client_session_id to hub.qs so reconnects across the same client can be correlated in diagnostics.
 * - Reconnection behavior
 *   - Replaced fixed 5s retry loops with exponential backoff + jitter (capped at 30s).
 *   - Online/offline aware: pauses reconnect attempts while offline; resumes automatically when the browser comes online.
 *   - Publishes connection state via connectionState$ BehaviorSubject for UI/service awareness.
 * - Diagnostics and telemetry
 *   - logTelemetry(event, data) centralizes structured logging (console today; can be routed server-side later).
 *   - Basic hub diagnostics: stateChanged/error logging and optional frame tap via hub.received().
 *   - Emits invoke_slow after 10s, invoke_ok/invoke_err with durations, and invoke_timeout on client-side timeouts.
 * - Invocation safety and UX
 *   - New invokeWithReconnectOnce(callFactory, timeoutMs, label):
 *       - gates on ensureStarted(),
 *       - retries once if the connection wasn’t active,
 *       - applies a hard timeout (default 3 minutes),
 *       - logs slow/ok/err/timeout telemetry,
 *       - shows a user-friendly timeout message via MessageService.
 *   - All exported server methods are wrapped with invokeWithReconnectOnce and labeled for per-call tuning and visibility.
 *   - Coalesces concurrent getReachableConnectedUsers() calls to avoid flooding the hub.
 * - Debugging and operability
 *   - Added debugStop(), debugStart(), and debugSetBaseUrl() helpers; exposes window.signalRService for console testing.
 *   - ensureHubProxy() rebinds the current proxy when $.connection.hub is replaced (e.g., after reloading /signalr/hubs).
 *
 * Timeout policy
 * - Default per-invocation timeout is 3 minutes (180,000 ms).
 * - Most operations should complete well under 10 seconds; those exceeding 10s emit invoke_slow telemetry.
 * - For legitimate long-running operations, pass a higher timeoutMs to invokeWithReconnectOnce for that call only.
 *
 * Security note
 * - SignalR 2.x requires tokens on the query string for transports like WebSockets. The token is read from sessionStorage (rpmsToken),
 *   which is cleared on logout and on browser session end (unchanged from previous behavior).
 */

import { Injectable, NgZone } from '@angular/core';
import * as $ from 'jquery';
import { NotificationWindowService } from './notification-window.service';
import { MessageService } from './message.service';
import 'signalR';
import { PriceManagementDataService } from '../price-management/price-management-data.service';
import { AuthenticationService } from '../core/authentication/shared/authentication.service';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { AppConfig } from '../app.config';

declare const $: any;

// Minimal jQuery-like promise shape used by SignalR invocations in this service
type JqPromise<T> = {
  done(cb: (data: T) => void): JqPromise<T>;
  fail(cb: (err: any) => void): JqPromise<T>;
};

type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private config = AppConfig.settings;

  // Auto-generated proxy reference for the hub (set in initSignalR)
  priceManagementHub: any;

  // True while the client is logging off (suppresses auto-reconnect)
  isLoggingOff: boolean = false;

  // One-shot start path: when null, a new start can be attempted
  private startPromise: Promise<void> | null = null;

  // Operational toggle to disable auto-reconnect for debug
  public suppressAutoReconnect: boolean = false;

  // Track reconnect attempts and offline state
  private reconnectAttempt = 0;
  private isOffline = !navigator.onLine;

  // App event stream
  scheduleChange$: Observable<any>;
  private _scheduleChangeSubject = new Subject<any>();

  // Connection state stream (new)
  connectionState$: Observable<{ state: ConnectionState; since: number; attempt?: number; reason?: string }>;
  private _connectionStateSubject =
    new BehaviorSubject<{ state: ConnectionState; since: number; attempt?: number; reason?: string }>({
      state: 'disconnected',
      since: Date.now()
    });

  // Single, consistent baseUrl used for both loading /signalr/hubs and $.connection.hub.url
  private hubBaseUrl: string = this.resolveBaseUrl();

  // Track current store for auto-rejoin on reconnect
  private currentStoreId: number | string | null = null;

  // Track last successful sync timestamps for delta resync after reconnect
  private lastStoreItemSyncTimestamp: Date | null = null;
  private lastPreviewStoreItemSyncTimestamp: Date | null = null;
  private lastMessageSyncTimestamp: Date | null = null;

  // Hub connection instance
  private hubConnection: any;
  private getUsersInflight: Promise<any> | null = null;

  /**
   * Resolve the base URL used to build the SignalR URL: <base>/signalr and <base>/signalr/hubs.
   * Priority:
   * 1) AppConfig.settings.baseUrl (if present),
   * 2) Fallback to the SPA origin (protocol + host) if AppConfig isn’t available in the current scope (e.g., console).
   */
  private resolveBaseUrl(): string {
    try {
      const cfg = (AppConfig as any)?.settings?.baseUrl;
      if (cfg && typeof cfg === 'string' && cfg.trim().length > 0) {
        return cfg.replace(/\/+$/, ''); // trim trailing slashes
      }
    } catch {
      // ignore and fallback
    }
    return `${location.protocol}//${location.host}`;
  }

  // Expose the service to the console for testability
  constructor(
    private _ngZone: NgZone,
    private _authenticationService: AuthenticationService,
    private _notificationWindowService: NotificationWindowService,
    private _priceManagementDataService: PriceManagementDataService,
    private _messageService: MessageService,
  ) {
    this.scheduleChange$ = this._scheduleChangeSubject.asObservable();
    this.connectionState$ = this._connectionStateSubject.asObservable();
    (window as any).signalRService = this; // debug helper

    // Monitor online/offline to pause/resume reconnect attempts
    window.addEventListener('online', () => {
      this.isOffline = false;
      this.logTelemetry('browser_online');
      // Allow a reconnect attempt shortly after coming back online
      this.reconnectAttempt = 0;
      this.tryReconnect('came_online');
    });
    window.addEventListener('offline', () => {
      this.isOffline = true;
      this.logTelemetry('browser_offline');
      this.publishState('disconnected', 'offline');
    });

    // Gracefully stop hub on page unload to avoid zombie connections
    window.addEventListener('beforeunload', () => {
      try { $.connection?.hub?.stop(); } catch { /* ignore */ }
    });
  }

  /**
   * Emits that a schedule change was detected, allowing subscribers to react (e.g., refresh a grid).
   */
  setScheduleChange() {
    this._scheduleChangeSubject.next(true);
  }

  /**
   * Returns current bearer token for SignalR query string auth.
   * Note: WebSockets do not send Authorization headers. SignalR 2.x reads the token from query string (access_token).
   */
  private getToken(): string | null {
    return sessionStorage.getItem('rpmsToken');
  }

  /**
   * Loads generated SignalR proxies from <base>/signalr/hubs and kicks off initialization.
   * Ensures the plugin/proxies bind to the same jQuery instance used by this service by assigning window.$/window.jQuery.
   * Exposes a debug surface (window.debugSignalR) for manual testing from the console.
   */
  loadSignalRHubs() {
    const self = this;
    (window as any).jQuery = (window as any).$ = $;

    // If proxies already exist, skip reloading script and just init
    if ($.connection && $.connection.priceManagement && $.connection.hub) {
      if (self.config.logSignalR) console.log('[SignalR] Proxies already present; skipping getScript.');
      this.startPromise = null; // ensure a fresh start on this hub instance
      self._notificationWindowService.setAreHubsLoaded(true);
      self.initSignalR();
      return;
    }

    $.getScript(this.hubBaseUrl + '/signalr/hubs?v=' + Date.now())
      .done(function () {
        if (self.config.logSignalR) console.log('[SignalR] Loaded /signalr/hubs from', self.hubBaseUrl);

        // Sanity checks before init
        if (!$.connection || !$.connection.hub) {
          console.error('[SignalR] jquery.signalR plugin not loaded (no $.connection.hub). Include jquery.signalR-x.y.z.js in angular.json scripts before app bundle.');
          (window as any).debugSignalR = {
            hub: () => $.connection,
            url: () => $.connection?.hub?.url,
            start: () => self.ensureStarted()
          };
          return;
        }
        if (!$.connection.priceManagement) {
          console.error('[SignalR] Generated proxy "priceManagement" missing on $.connection. Check EnableJavaScriptProxies=true and baseUrl:', self.hubBaseUrl);
          (window as any).debugSignalR = {
            hub: () => $.connection,
            url: () => $.connection?.hub?.url,
            start: () => self.ensureStarted()
          };
          return;
        }

        self._notificationWindowService.setAreHubsLoaded(true);

        // IMPORTANT: proxies just loaded can replace $.connection.hub; force a fresh start
        self.startPromise = null;

        self.initSignalR();

        // Expose a console helper for manual verification
        (window as any).debugSignalR = {
          hub: () => $.connection,
          url: () => $.connection?.hub?.url,
          start: () => self.ensureStarted()
        };
      })
      .fail(function (xhr, status, err) {
        console.error('[SignalR] Failed to load /signalr/hubs:', status, err, xhr?.status, xhr?.responseText, 'from', self.hubBaseUrl);
      });
  }

  /**
   * Wires the client handlers for the priceManagement hub and establishes lifecycle handlers.
   * Starts the hub once via ensureStarted().
   */
  initSignalR() {
    const self = this;
    if (self.config.logSignalR) console.log('[SignalR] initSignalR() called, registering client handlers...');

    if (!$.connection || !$.connection.priceManagement) {
      console.error('[SignalR] Proxy not available in initSignalR; aborting init.');
      return;
    }

    // Reference the auto-generated proxy for the hub
    this.priceManagementHub = $.connection.priceManagement;
    this.hubConnection = $.connection.hub;
    if (this.config.logSignalR) console.log('[SignalR] priceManagementHub.client:', this.priceManagementHub?.client);

    // ----- Client-to-UI handlers -----
    this.priceManagementHub.client.userConnected = function (_userInfo) {
      if (self.config.logSignalR) console.log('[SignalR] userConnected:', _userInfo);
      self.getReachableConnectedUsers(
        function (data) { self._notificationWindowService.setReachableUsers(data); },
        function (response) { self._messageService.onFailure('Failed to get reachable users.', response); });
    };

    this.priceManagementHub.client.userDisconnected = function (_userInfo) {
      if (self.config.logSignalR) console.log('[SignalR] userDisconnected:', _userInfo);
      self.getReachableConnectedUsers(
        function (data) { self._notificationWindowService.setReachableUsers(data); },
        function (response) { self._messageService.onFailure('Failed to get reachable users.', response); });
    };

    this.priceManagementHub.client.userReconnected = function (_userInfo) {
      if (self.config.logSignalR) console.log('[SignalR] userReconnected:', _userInfo);
      self.getReachableConnectedUsers(
        function (data) { self._notificationWindowService.setReachableUsers(data); },
        function (response) { self._messageService.onFailure('Failed to get reachable users.', response); });
    };

    this.priceManagementHub.client.systemNotification = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] systemNotification:', data);
      self._notificationWindowService.createSystemNotification(data);
    };

    this.priceManagementHub.client.userNotification = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] userNotification:', data);
      self._notificationWindowService.createUserNotification(data);
    };

    this.priceManagementHub.client.userWorkingInStore = function (userInfo) {
      try {
        if (self.config.logSignalR) console.log('[SignalR] userWorkingInStore:', userInfo);
        const myUser = JSON.parse(sessionStorage.getItem('rpmsUser'));
        if (self.config.logSignalR) console.log('[SignalR] userWorkingInStore comparing:', userInfo?.user?.userId, 'vs myUser:', myUser?.userId);
        if (userInfo.user.userId != myUser.userId) {
          self._notificationWindowService.createUserWorkingInStoreNotification(userInfo);
        } else {
          if (self.config.logSignalR) console.log('[SignalR] userWorkingInStore filtered (same user)');
        }
      } catch (err) {
        console.error('[SignalR] userWorkingInStore handler error:', err);
      }
      self.getReachableConnectedUsers(
        function (data) { self._notificationWindowService.setReachableUsers(data); },
        function (response) { self._messageService.onFailure('Failed to get reachable users.', response); });
    };
    if (this.config.logSignalR) console.log('[SignalR] userWorkingInStore handler REGISTERED on:', this.priceManagementHub?.client);

    this.priceManagementHub.client.userExitedStore = function (userInfo, store) {
      if (self.config.logSignalR) console.log('[SignalR] userExitedStore:', { userInfo, store });
      self._notificationWindowService.createUserExitedStoreNotification(userInfo, store);
      self.getReachableConnectedUsers(
        function (data) { self._notificationWindowService.setReachableUsers(data); },
        function (response) { self._messageService.onFailure('Failed to get reachable users.', response); });
    };

    this.priceManagementHub.client.storeItemPriceChanged = function (user, storeItem) {
      if (self.config.logSignalR) console.log('[SignalR] storeItemPriceChanged:', { userName: user?.userName, itemCode: storeItem?.item?.itemCode, storeId: storeItem?.storeId, user, storeItem });
      self._notificationWindowService.createItemUpdateNotification(user, storeItem);
      self._priceManagementDataService.setSignalRStoreItemUpdate(storeItem);
    };

    this.priceManagementHub.client.storeItemTprTagUpdated = function (user, data) {
      if (self.config.logSignalR) console.log('[SignalR] storeItemTprTagUpdated:', { user, data });
      self._notificationWindowService.createStoreItemTprTagUpdatedNotification(user, data);
      self._priceManagementDataService.setSignalRStoreItemUpdate(data);
    };

    this.priceManagementHub.client.storeItemShelfTagUpdated = function (user, data) {
      if (self.config.logSignalR) console.log('[SignalR] storeItemShelfTagUpdated:', { user, data });
      self._notificationWindowService.createStoreItemShelfTagUpdatedNotification(user, data);
      self._priceManagementDataService.setSignalRStoreItemUpdate(data);
    };

    this.priceManagementHub.client.storeItemDeleted = function (user, storeItem) {
      if (self.config.logSignalR) console.log('[SignalR] storeItemDeleted:', { user, storeItem });
      self._notificationWindowService.createItemUpdateNotification(user, storeItem);
      self._priceManagementDataService.setSignalRStoreItemUpdate(storeItem);
    };

    this.priceManagementHub.client.previewStoreItemPriceChanged = function (user, previewStoreItem) {
      if (self.config.logSignalR) console.log('[SignalR] previewStoreItemPriceChanged:', { userName: user?.userName, itemCode: previewStoreItem?.item?.itemCode, storeId: previewStoreItem?.storeId, user, previewStoreItem });
      self._notificationWindowService.createItemUpdateNotification(user, previewStoreItem);
      self._priceManagementDataService.setSignalRStoreItemUpdate(previewStoreItem);
    };

    this.priceManagementHub.client.previewStoreItemPriceChangesEnabledStateChange = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] previewStoreItemPriceChangesEnabledStateChange:', data);
      self._notificationWindowService.createPreviewStoreItemPriceChangesEnabledStateChangeNotification(data);
      self._priceManagementDataService.setPreviewStoreItemPriceChangesEnabledForStoreObject(data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.meatPreviewStoreItemPriceChangesEnabledStateChange = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] meatPreviewStoreItemPriceChangesEnabledStateChange:', data);
      self._notificationWindowService.createMeatPreviewStoreItemPriceChangesEnabledStateChangeNotification(data);
      self._priceManagementDataService.setMeatPreviewStoreItemPriceChangesEnabledForStoreObject(data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.producePreviewStoreItemPriceChangesEnabledStateChange = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] producePreviewStoreItemPriceChangesEnabledStateChange:', data);
      self._notificationWindowService.createProducePreviewStoreItemPriceChangesEnabledStateChangeNotification(data);
      self._priceManagementDataService.setProducePreviewStoreItemPriceChangesEnabledForStoreObject(data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.storeItemPriceChangesEnabledStateChange = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] storeItemPriceChangesEnabledStateChange:', data);
      self._notificationWindowService.createStoreItemPriceChangesEnabledStateChangeNotification(data);
      self._priceManagementDataService.setStoreItemPriceChangesEnabledForStoreObject(data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.meatStoreItemPriceChangesEnabledStateChange = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] meatStoreItemPriceChangesEnabledStateChange:', data);
      self._notificationWindowService.createMeatStoreItemPriceChangesEnabledStateChangeNotification(data);
      self._priceManagementDataService.setMeatStoreItemPriceChangesEnabledForStoreObject(data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.produceStoreItemPriceChangesEnabledStateChange = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] produceStoreItemPriceChangesEnabledStateChange:', data);
      self._notificationWindowService.createProduceStoreItemPriceChangesEnabledStateChangeNotification(data);
      self._priceManagementDataService.setProduceStoreItemPriceChangesEnabledForStoreObject(data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.serverMaintenanceEnabledStateChange = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] serverMaintenanceEnabledStateChange:', data);
      self._notificationWindowService.createServerMaintenanceEnabledStateChangeNotification(data);
      self._priceManagementDataService.setServerMaintenanceEnabledStateChangeObject(data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.deletingMeatAndProducePreviewStoreItems = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] deletingMeatAndProducePreviewStoreItems:', data);
      self._notificationWindowService.createSystemMessageNotification(data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
      // This event fires twice — once when the job starts (Processing) and once when it finishes (Processed).
      // We only want to reload the grid after the job is done, not while it is still running.
      if (data.taskState === 'Processed') {
        self._priceManagementDataService.triggerRefreshDataFromServer();
      }
    };

    this.priceManagementHub.client.incrementingWeeksInStore = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] incrementingWeeksInStore:', data);
      self._notificationWindowService.createSystemMessageNotification(data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.purgingOldLogActions = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] purgingOldLogActions:', data);
      self._notificationWindowService.createSystemMessageNotification(data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.purgingOldMessages = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] purgingOldMessages:', data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.purgingOldAdItems = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] purgingOldAdItems:', data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.purgingOldStoreItemPriceChanges = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] purgingOldStoreItemPriceChanges:', data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.purgingOldStoreItemCostChanges = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] purgingOldStoreItemCostChanges:', data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.purgingOldDenormalizedPreviewStoreItemPriceChanges = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] purgingOldDenormalizedPreviewStoreItemPriceChanges:', data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.processingPricingRules = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] processingPricingRules:', data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.resettingMovementAndWeeksInStore = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] resettingMovementAndWeeksInStore:', data);
      self._notificationWindowService.createSystemMessageNotification(data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.updatingStoreItemsFromMeatAndProducePreviewStoreItems = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] updatingStoreItemsFromMeatAndProducePreviewStoreItems:', data);
      self._notificationWindowService.createSystemMessageNotification(data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.updatingStoreItemsFromPreviewStoreItems = function (data) {
      if (self.config.logSignalR) console.log('[SignalR] updatingStoreItemsFromPreviewStoreItems:', data);
      self._notificationWindowService.createSystemMessageNotification(data);
      self._messageService.showToastBottomLeft(data.reason);
      self.setScheduleChange();
    };

    this.priceManagementHub.client.scheduleEvent = function (event, reason) {
      if (self.config.logSignalR) console.log('[SignalR] scheduleEvent:', event, reason);
      self._messageService.showToastBottomLeft(reason);
      self.setScheduleChange();
    };

    // ----- End client handlers -----

    // Wrap every registered client handler so it runs inside Angular's zone.
    // SignalR callbacks fire from jQuery outside NgZone; without this, Angular
    // change detection does not run and NG0100 errors occur.
    const client = this.priceManagementHub.client;
    Object.keys(client).forEach(key => {
      const original = client[key];
      if (typeof original === 'function') {
        client[key] = function (...args: any[]) {
          self._ngZone.run(() => original.apply(this, args));
        };
      }
    });

    // Connection lifecycle handlers (set once)

    $.connection.hub.connectionSlow(() => {
      this.logTelemetry('connection_slow');
      console.log('[SignalR] connection is slow.');
    });

    $.connection.hub.reconnecting(() => {
      this.publishState('reconnecting');
      this.logTelemetry('reconnecting');
      console.log('[SignalR] reconnecting...');
    });

    $.connection.hub.reconnected(() => {
      this.reconnectAttempt = 0;
      this.publishState('connected');
      this.logTelemetry('reconnected');
      if (this.config.logSignalR) console.log('[SignalR] reconnected (native).');

      // Unified post-reconnect sequence: refresh token, rejoin store, resync data
      this.refreshHubToken();
      if (this.currentStoreId !== null) {
        if (this.config.logSignalR) console.log('[SignalR] rejoining store group after reconnect:', this.currentStoreId);
        this.rejoinStoreGroup(this.currentStoreId);
      }
      this.resyncAfterReconnect();
    });

    $.connection.hub.disconnected(() => {
      this.publishState('disconnected');
      this.logTelemetry('disconnected');
      if (!self.isLoggingOff && !self.suppressAutoReconnect) {
        this.tryReconnect('disconnected');
      }
    });

    // Start once via ensureStarted (no direct .start)
    this.ensureStarted()
      .then(() => {
        if (self.config.logSignalR) console.log('[SignalR] hub started.');
        this.publishState('connected');
        if (self._authenticationService.isUserLoggedIn) {
          self.getReachableConnectedUsers(
            data => self._notificationWindowService.setReachableUsers(data),
            resp => self._messageService.onFailure('Failed to get reachable users.', resp)
          );
        }
      })
      .catch(err => {
        console.error('[SignalR] initial start failed:', err);
        this.publishState('disconnected', 'start_failed');
        this.tryReconnect('start_failed');
      });
  }

  /**
   * Ensures the connection is started once and returns a promise that resolves when connected.
   * All hub method invocations should call this first.
   */
  ensureStarted(): Promise<void> {
    if (this.config.logSignalR) console.log('[SignalR] ensureStarted() state:', $.connection?.hub?.state, 'url:', $.connection?.hub?.url);

    if (this.startPromise) return this.startPromise;

    // Force consistent URL and logging
    $.connection.hub.logging = true;
    $.connection.hub.url = this.hubBaseUrl + '/signalr';

    // Attach token via query string so it flows over all transports 
    const token = this.getToken();

    // Attach a client session id for correlation across reconnects
    const sessionId = this.getOrCreateSessionId();

    $.connection.hub.qs = {
      'access_token': token,
      'client_session_id': sessionId
    };

    this.publishState('connecting');
    this.logTelemetry('starting', { url: $.connection.hub.url, hasToken: !!token });

    // Basic diagnostics
    $.connection.hub.error(err => console.error('[SignalR] error:', err));
    $.connection.hub.stateChanged(s => { if (this.config.logSignalR) console.log('[SignalR] state:', s.oldState, '->', s.newState); });

    // Optional tap to see raw inbound messages (frames)
    $.connection.hub.received((msg: any) => {
      if (this.config.logSignalR) console.log('[SignalR] <<', msg);
      // Log all hub method invocations (M = method name, A = args array)
      if (msg && msg.M) {
        if (this.config.logSignalR) console.log('[SignalR] RAW HUB MESSAGE:', msg.M, msg.A);
      }
    });

    // Single start path
    this.startPromise = new Promise<void>((resolve, reject) => {
      const startedAt = Date.now();
      $.connection.hub
        .start({ transport: ['webSockets', 'serverSentEvents', 'longPolling'] })
        .done(() => {
          // Rebind proxy to the (possibly replaced) hub instance
          this.priceManagementHub = $.connection.priceManagement;
          this.hubConnection = $.connection.hub;
          const duration = Date.now() - startedAt;
          this.logTelemetry('started', { id: $.connection.hub.id, durationMs: duration });
          console.log('[SignalR] connected. id:', $.connection.hub.id, 'url:', $.connection.hub.url);
          resolve();
        })
        .fail(err => {
          const duration = Date.now() - startedAt;
          this.logTelemetry('start_failed', { durationMs: duration, error: this.stringifyErr(err) });
          console.error('[SignalR] start failed:', err);
          this.startPromise = null; // allow retry
          reject(err);
        });
    });

    return this.startPromise;
  }

  /**
   * Attempts reconnect with exponential backoff and jitter.
   */
  private tryReconnect(reason: string) {
    if (this.isOffline) {
      console.warn('[SignalR] offline; will not attempt reconnect until online.');
      return;
    }
    this.reconnectAttempt++;
    const base = Math.min(30000, 2000 * Math.pow(1.5, this.reconnectAttempt)); // cap 30s
    const jitter = Math.floor(Math.random() * 500); // +0-500ms
    const delay = base + jitter;
    console.warn(`[SignalR] reconnect attempt #${this.reconnectAttempt} in ${delay}ms (reason=${reason})`);
    this.publishState('reconnecting', reason, this.reconnectAttempt);
    setTimeout(() => {
      this.startPromise = null;
      this.ensureStarted()
        .then(() => {
          this.reconnectAttempt = 0;
          this.publishState('connected');
          if (this.config.logSignalR) console.log('[SignalR] reconnected (manual tryReconnect).');

          // Unified post-reconnect sequence: refresh token, rejoin store, resync data
          this.refreshHubToken();
          if (this.currentStoreId !== null) {
            if (this.config.logSignalR) console.log('[SignalR] rejoining store group after manual reconnect:', this.currentStoreId);
            this.rejoinStoreGroup(this.currentStoreId);
          }
          this.resyncAfterReconnect();
        })
        .catch(err => {
          this.logTelemetry('reconnect_failed', { error: this.stringifyErr(err) });
          console.error('[SignalR] reconnect failed:', err);
          // Will try again on next disconnection event or external trigger
        });
    }, delay);
  }

  /**
   * Exits the current store on the server side.
   * Wrapped to ensure timeout/telemetry and one restart-once retry.
   */
  exitStore(onSuccessCallback?, onFailureCallback?) {
    const self = this;
    // If the hub connection is already stopped or stopping, exit silently.
    // This avoids "Connection must be started before data can be sent" during logout.
    const state = $.connection?.hub?.state;
    if (self.isLoggingOff || state === undefined || state === $.signalR?.connectionState?.disconnected) {
      self.currentStoreId = null;
      if (typeof onSuccessCallback === 'function') onSuccessCallback(null);
      return;
    }
    this.ensureStarted().then(() => {
      const call = () => this.ensureHubProxy().server.exitStore();
      this.invokeWithReconnectOnce(call, 30000, 'exitStore')
        .then((data: any) => {
          // Clear tracked store so we don't rejoin on reconnect
          self.currentStoreId = null;
          if (self.config.logSignalR) console.log('[SignalR] exit store was successful');
          if (typeof onSuccessCallback === 'function') onSuccessCallback(data);
        })
        .catch((error: any) => {
          console.log('[SignalR] send error:', error);
          if (typeof onFailureCallback === 'function') onFailureCallback(error);
        });
    }).catch(err => console.error('[SignalR] exitStore ensureStarted failed:', err));
  }

  /**
   * Enters the given store on the server so the user receives notifications scoped to that store.
   * Wrapped; store enter should be fast; 30s is ample.
   */
  workInStore(storeId, onSuccessCallback?, onFailureCallback?) {
    const self = this;
    if (self.config.logSignalR) console.log('[SignalR] workInStore() called with storeId:', storeId);
    this.ensureStarted().then(() => {
      const call = () => this.ensureHubProxy().server.workInStore(storeId);
      this.invokeWithReconnectOnce(call, 30000, 'workInStore')
        .then((data: any) => {
          // Track current store for auto-rejoin on reconnect
          self.currentStoreId = storeId;
          if (self.config.logSignalR) console.log('[SignalR] workInStore SUCCEEDED, now in group for store:', storeId, 'response:', data);
          if (self.config.logSignalR) console.log('[SignalR] work in store was successful');
          if (typeof onSuccessCallback === 'function') onSuccessCallback(data);
        })
        .catch((error: any) => {
          console.log('[SignalR] workInStore FAILED:', error);
          if (typeof onFailureCallback === 'function') onFailureCallback(error);
        });
    }).catch(err => console.error('[SignalR] workInStore ensureStarted failed:', err));
  }

  /**
   * Internal: rejoin a store group after reconnect (no callbacks, silent).
   */
  private rejoinStoreGroup(storeId: number | string) {
    const self = this;
    this.ensureStarted().then(() => {
      const call = () => this.ensureHubProxy().server.workInStore(storeId);
      this.invokeWithReconnectOnce(call, 30000, 'rejoinStoreGroup')
        .then(() => {
          if (self.config.logSignalR) console.log('[SignalR] rejoinStoreGroup SUCCEEDED for store:', storeId);
        })
        .catch((error: any) => {
          console.error('[SignalR] rejoinStoreGroup FAILED:', error);
        });
    }).catch(err => console.error('[SignalR] rejoinStoreGroup ensureStarted failed:', err));
  }

  /**
   * Retrieves the list of reachable connected users.
   * Wrapped; default 3 min cap. Adjust per your data volume if needed.
   */
  getReachableConnectedUsers(onSuccessCallback?, onFailureCallback?) {
    const call = () => {
      const hub = this.ensureHubProxy();
      return hub.server.getReachableConnectedUsers();
    };

    // Coalesce concurrent calls to avoid flooding the hub
    if (!this.getUsersInflight) {
      const p = this.invokeWithReconnectOnce(call, 180000, 'getReachableConnectedUsers');
      this.getUsersInflight = p;
      // Reset inflight regardless of outcome
      p.then(
        () => { this.getUsersInflight = null; },
        () => { this.getUsersInflight = null; }
      );
    }

    this.getUsersInflight
      .then(data => {
        if (this.config.logSignalR) console.log('[SignalR] getReachableConnectedUsers ok');
        if (typeof onSuccessCallback === 'function') onSuccessCallback(data);
      })
      .catch(error => {
        console.log('[SignalR] getReachableConnectedUsers error:', error);
        if (typeof onFailureCallback === 'function') onFailureCallback(error);
      });
  }

  /**
   * Retrieves all messages associated with the user.
   * Wrapped; likely fast to moderate; 3 min default is safe.
   */
  synchronizeMessages(onSuccessCallback?, onFailureCallback?) {
    const self = this;
    this.ensureStarted().then(() => {
      const call = () => this.ensureHubProxy().server.synchronizeMessages();
      this.invokeWithReconnectOnce(call, 180000, 'synchronizeMessages')
        .then((data: any) => {
          if (self.config.logSignalR) console.log('[SignalR] synchronizeMessages was successful.');
          self.lastMessageSyncTimestamp = new Date();
          if (typeof onSuccessCallback === 'function') onSuccessCallback(data);
        })
        .catch((error: any) => {
          console.log('[SignalR] send error:', error);
          if (typeof onFailureCallback === 'function') onFailureCallback(error);
        });
    }).catch(err => console.error('[SignalR] synchronizeMessages ensureStarted failed:', err));
  }

  /**
   * Retrieves store items updated since the specified timestamp.
   * Wrapped; can be heavy in some cases; keep 3 min default, increase per-call if needed.
   */
  synchronizeStoreItems(timeStamp, onSuccessCallback?, onFailureCallback?) {
    const self = this;
    this.ensureStarted().then(() => {
      const call = () => this.ensureHubProxy().server.synchronizeStoreItems(timeStamp);
      this.invokeWithReconnectOnce(call, 180000, 'synchronizeStoreItems')
        .then((data: any) => {
          self.lastStoreItemSyncTimestamp = new Date();
          if (typeof onSuccessCallback === 'function') onSuccessCallback(data);
        })
        .catch((error: any) => {
          console.log('[SignalR] send error:', error);
          if (typeof onFailureCallback === 'function') onFailureCallback(error);
        });
    }).catch(err => console.error('[SignalR] synchronizeStoreItems ensureStarted failed:', err));
  }

  /**
   * Retrieves preview store items updated since the specified timestamp.
   * Wrapped; keep default 3 min unless your telemetry shows it’s always quick.
   */
  synchronizePreviewStoreItems(timeStamp, onSuccessCallback?, onFailureCallback?) {
    const self = this;
    this.ensureStarted().then(() => {
      const call = () => this.ensureHubProxy().server.synchronizePreviewStoreItems(timeStamp);
      this.invokeWithReconnectOnce(call, 180000, 'synchronizePreviewStoreItems')
        .then((data: any) => {
          self.lastPreviewStoreItemSyncTimestamp = new Date();
          if (typeof onSuccessCallback === 'function') onSuccessCallback(data);
        })
        .catch((error: any) => {
          console.log('[SignalR] send error:', error);
          if (typeof onFailureCallback === 'function') onFailureCallback(error);
        });
    }).catch(err => console.error('[SignalR] synchronizePreviewStoreItems ensureStarted failed:', err));
  }

  /**
   * Retrieves preview store items (Lite) updated since the specified timestamp.
   * Wrapped; typically lighter; leave at 3 min for consistency (adjust if needed).
   */
  synchronizePreviewStoreItemsV2(timeStamp, onSuccessCallback?, onFailureCallback?) {
    const self = this;
    this.ensureStarted().then(() => {
      const call = () => this.ensureHubProxy().server.synchronizePreviewStoreItemsV2(timeStamp);
      this.invokeWithReconnectOnce(call, 180000, 'synchronizePreviewStoreItemsV2')
        .then((data: any) => {
          self.lastPreviewStoreItemSyncTimestamp = new Date();
          if (typeof onSuccessCallback === 'function') onSuccessCallback(data);
        })
        .catch((error: any) => {
          console.log('[SignalR] send error:', error);
          if (typeof onFailureCallback === 'function') onFailureCallback(error);
        });
    }).catch(err => console.error('[SignalR] synchronizePreviewStoreItemsV2 ensureStarted failed:', err));
  }

  /**
   * Sends a message to all users within a store.
   * Wrapped; these are quick; 30s is ample.
   */
  sendMessageWithinStore(id, message, bool, onSuccessCallback?, onFailureCallback?) {
    const self = this;
    this.ensureStarted().then(() => {
      const call = () => this.ensureHubProxy().server.sendMessageWithinStore(id, message, bool);
      this.invokeWithReconnectOnce(call, 30000, 'sendMessageWithinStore')
        .then((data: any) => {
          if (self.config.logSignalR) console.log('[SignalR] sendMessageWithinStore was successful.');
          if (typeof onSuccessCallback === 'function') onSuccessCallback(data);
        })
        .catch((error: any) => {
          console.log('[SignalR] send error:', error);
          if (typeof onFailureCallback === 'function') onFailureCallback(error);
        });
    }).catch(err => console.error('[SignalR] sendMessageWithinStore ensureStarted failed:', err));
  }

  /**
   * Sends a message to all users within the store group of a store.
   * Wrapped; quick; 30s is ample.
   */
  sendMessageWithinStoreGroup(id, message, bool, onSuccessCallback?, onFailureCallback?) {
    const self = this;
    this.ensureStarted().then(() => {
      const call = () => this.ensureHubProxy().server.sendMessageWithinStoreGroup(id, message, bool);
      this.invokeWithReconnectOnce(call, 30000, 'sendMessageWithinStoreGroup')
        .then((data: any) => {
          if (self.config.logSignalR) console.log('[SignalR] sendMessageWithinStoreGroup was successful.');
          if (typeof onSuccessCallback === 'function') onSuccessCallback(data);
        })
        .catch((error: any) => {
          console.log('[SignalR] send error:', error);
          if (typeof onFailureCallback === 'function') onFailureCallback(error);
        });
    }).catch(err => console.error('[SignalR] sendMessageWithinStoreGroup ensureStarted failed:', err));
  }

  /**
   * Sends a message to a specific user by connection id.
   * Wrapped; quick; 30s is ample.
   */
  sendMessageToUser(id, message, bool, onSuccessCallback?, onFailureCallback?) {
    const self = this;
    this.ensureStarted().then(() => {
      const call = () => this.ensureHubProxy().server.sendMessageToUser(id, message, bool);
      this.invokeWithReconnectOnce(call, 30000, 'sendMessageToUser')
        .then((data: any) => {
          if (self.config.logSignalR) console.log('[SignalR] sendMessageToUser was successful.');
          if (typeof onSuccessCallback === 'function') onSuccessCallback(data);
        })
        .catch((error: any) => {
          console.log('[SignalR] send error:', error);
          if (typeof onFailureCallback === 'function') onFailureCallback(error);
        });
    }).catch(err => console.error('[SignalR] sendMessageToUser ensureStarted failed:', err));
  }

  /**
   * Sends an admin broadcast message.
   * Wrapped; quick; 30s is ample.
   */
  sendAdminMessage(message, onSuccessCallback?, onFailureCallback?) {
    const self = this;
    this.ensureStarted().then(() => {
      const call = () => this.ensureHubProxy().server.sendAdminMessage(message);
      this.invokeWithReconnectOnce(call, 30000, 'sendAdminMessage')
        .then((data: any) => {
          if (self.config.logSignalR) console.log('[SignalR] sendAdminMessage was successful.');
          if (typeof onSuccessCallback === 'function') onSuccessCallback(data);
        })
        .catch((error: any) => {
          console.log('[SignalR] send error:', error);
          if (typeof onFailureCallback === 'function') onFailureCallback(error);
        });
    }).catch(err => console.error('[SignalR] sendAdminMessage ensureStarted failed:', err));
  }

  /**
   * Helper that runs a hub invocation, retries once if the connection was transiently disconnected,
   * and fails with a timeout (default 3 min) if the server never returns a result.
   * Also logs:
   * - invoke_slow after 10 seconds (to help locate calls that usually exceed 10s),
   * - invoke_ok with duration,
   * - invoke_err with duration and error,
   * - and shows a user-friendly error when a timeout occurs.
   */
  private invokeWithReconnectOnce<T>(
    callFactory: () => JqPromise<T>,
    timeoutMs = 180000, // 3 minutes default
    label = 'invoke'
  ): Promise<T> {
    return this.ensureStarted().then(() => new Promise<T>((resolve, reject) => {
      const started = Date.now();
      let done = false;

      // Soft “slow-call” hint at 10 seconds to help identify which operations commonly exceed 10s.
      const slowThresholdMs = 10000;
      const slowTimer = setTimeout(() => {
        if (!done) this.logTelemetry('invoke_slow', { label, thresholdMs: slowThresholdMs });
      }, slowThresholdMs);

      // Hard timeout with a user-facing message at timeoutMs (default 3 minutes)
      const friendlyTimeout = (ms: number) => {
        if (ms % 60000 === 0) return `${ms / 60000} minute${ms === 60000 ? '' : 's'}`;
        if (ms >= 60000) return `${(ms / 60000).toFixed(1)} minutes`;
        return `${Math.ceil(ms / 1000)} seconds`;
      };

      const timer = setTimeout(() => {
        if (!done) {
          done = true;
          clearTimeout(slowTimer);
          this.logTelemetry('invoke_timeout', { label, timeoutMs });
          // User-friendly timeout message
          this._messageService.onFailure(
            `The request ${label ? `"${label}" ` : ''}did not complete within ${friendlyTimeout(timeoutMs)} and was cancelled. Please try again.`,
            { timeoutMs }
          );
          reject(new Error('SignalR invocation timed out'));
        }
      }, timeoutMs);

      const finishOk = (v: T) => {
        if (!done) {
          done = true;
          clearTimeout(timer);
          clearTimeout(slowTimer);
          this.logTelemetry('invoke_ok', { label, durationMs: Date.now() - started });
          resolve(v);
        }
      };

      const finishErr = (e: any) => {
        if (!done) {
          done = true;
          clearTimeout(timer);
          clearTimeout(slowTimer);
          this.logTelemetry('invoke_err', { label, durationMs: Date.now() - started, error: this.stringifyErr(e) });
          reject(e);
        }
      };

      const run = () => {
        const proxy = this.ensureHubProxy();
        if (!proxy) { finishErr(new Error('SignalR proxy not available')); return; }

        callFactory()
          .done(finishOk)
          .fail((err: any) => {
            const msg = (typeof err === 'string' ? err : (err?.Message || err?.message || err?.statusText || '')) + '';
            const lower = msg.toLowerCase();
            const looksNotStarted = lower.includes('must be started before data can be sent') ||
              lower.includes('connection is not active') ||
              $.connection?.hub?.state !== 1;

            if (looksNotStarted) {
              // Restart and rebind once, then retry
              this.startPromise = null;
              this.ensureStarted()
                .then(() => callFactory().done(finishOk).fail(finishErr))
                .catch(finishErr);
            } else {
              finishErr(err);
            }
          });
      };
      run();
    }));
  }

  /**
   * Debug helper: stop the hub and suppress auto-reconnect.
   */
  public debugStop(): void {
    try {
      this.suppressAutoReconnect = true;
      this.startPromise = null;
      if ($.connection?.hub?.state !== undefined) {
        $.connection.hub.stop();
        this.publishState('disconnected', 'debugStop');
        console.log('[SignalR] debugStop: hub stopped, auto-reconnect suppressed.');
      }
    } catch (e) {
      console.warn('[SignalR] debugStop error:', e);
    }
  }

  /**
   * Debug helper: start the hub (clears suppression).
   */
  public debugStart(): Promise<void> {
    this.suppressAutoReconnect = false;
    this.startPromise = null;
    return this.ensureStarted().then(() => {
      console.log('[SignalR] debugStart: hub started. id:', $.connection.hub.id);
    });
  }

  /**
   * Debug helper: set a new base URL at runtime, stop and restart SignalR with the new endpoint.
   * Useful when testing from the console where AppConfig is not in scope.
   * Remove if not needed in production.
   */
  public debugSetBaseUrl(url: string): Promise<void> {
    const normalized = (url || '').replace(/\/+$/, '');
    if (!/^https?:\/\//i.test(normalized)) {
      console.warn('[SignalR] debugSetBaseUrl expects an absolute URL, got:', url);
    }
    this.hubBaseUrl = normalized || this.resolveBaseUrl();
    this.startPromise = null;
    try { if ($.connection?.hub?.state !== undefined) $.connection.hub.stop(); } catch { }
    return this.ensureStarted().then(() => {
      console.log('[SignalR] debugSetBaseUrl applied. hub.url:', $.connection.hub.url);
    });
  }

  /**
   * Refreshes the access_token on the SignalR query string so reconnections
   * use the latest token from sessionStorage. Call before hub invocations
   * after a reconnect, in case the auth service refreshed the token while
   * the connection was down.
   */
  private refreshHubToken(): void {
    try {
      const token = this.getToken();
      if ($.connection?.hub?.qs) {
        $.connection.hub.qs['access_token'] = token;
        if (this.config.logSignalR) console.log('[SignalR] refreshHubToken: token updated on hub.qs');
      }
    } catch (e) {
      console.warn('[SignalR] refreshHubToken error:', e);
    }
  }

  /**
   * Re-synchronizes data that may have been missed while the connection was down.
   * Uses the stored timestamps to request only deltas.
   * Called after any successful reconnect (both native and manual).
   */
  private resyncAfterReconnect(): void {
    const self = this;

    // 1. Refresh the connected users list
    if (this._authenticationService.isUserLoggedIn) {
      this.getReachableConnectedUsers(
        data => self._notificationWindowService.setReachableUsers(data),
        resp => console.warn('[SignalR] resync: getReachableConnectedUsers failed:', resp)
      );
    }

    // Only resync data if we're working in a store (otherwise there's nothing to sync)
    if (this.currentStoreId === null) {
      if (this.config.logSignalR) console.log('[SignalR] resyncAfterReconnect: not in a store, skipping data resync.');
      return;
    }

    // 2. Resync messages if we have a previous timestamp
    if (this.lastMessageSyncTimestamp) {
      if (this.config.logSignalR) console.log('[SignalR] resyncAfterReconnect: syncing messages since', this.lastMessageSyncTimestamp);
      this.synchronizeMessages(
        (data: any) => {
          if (data?.successful) {
            self.lastMessageSyncTimestamp = new Date();
            if (self.config.logSignalR) console.log('[SignalR] resyncAfterReconnect: messages synced, count:', data.messages?.length);
          }
        },
        (err: any) => console.warn('[SignalR] resyncAfterReconnect: synchronizeMessages failed:', err)
      );
    }

    // 3. Resync store items if we have a previous timestamp
    if (this.lastStoreItemSyncTimestamp) {
      if (this.config.logSignalR) console.log('[SignalR] resyncAfterReconnect: syncing store items since', this.lastStoreItemSyncTimestamp);
      this.synchronizeStoreItems(this.lastStoreItemSyncTimestamp,
        (data: any) => {
          if (data?.successful) {
            self.lastStoreItemSyncTimestamp = new Date();
            if (self.config.logSignalR) console.log('[SignalR] resyncAfterReconnect: store items synced, count:', data.storeItems?.length);
          }
        },
        (err: any) => console.warn('[SignalR] resyncAfterReconnect: synchronizeStoreItems failed:', err)
      );
    }

    // 4. Resync preview store items if we have a previous timestamp
    if (this.lastPreviewStoreItemSyncTimestamp) {
      if (this.config.logSignalR) console.log('[SignalR] resyncAfterReconnect: syncing preview items since', this.lastPreviewStoreItemSyncTimestamp);
      this.synchronizePreviewStoreItemsV2(this.lastPreviewStoreItemSyncTimestamp,
        (data: any) => {
          if (data?.successful) {
            self.lastPreviewStoreItemSyncTimestamp = new Date();
            if (self.config.logSignalR) console.log('[SignalR] resyncAfterReconnect: preview items synced, count:', data.previewStoreItems?.length);
          }
        },
        (err: any) => console.warn('[SignalR] resyncAfterReconnect: synchronizePreviewStoreItemsV2 failed:', err)
      );
    }

    this._messageService.showToastBottomLeft('Connection restored. Re-synchronizing data...');
  }

  // helper to always use the current hub/proxy
  private ensureHubProxy(): any {
    // If proxies aren't available or the proxy is bound to a different hub instance, rebind
    if (!$.connection || !$.connection.hub || !$.connection.priceManagement) {
      console.error('[SignalR] ensureHubProxy: proxies/hub not available.');
      return null;
    }
    if (!this.priceManagementHub || this.priceManagementHub.connection !== $.connection.hub) {
      if (this.config.logSignalR) console.log('[SignalR] Rebinding hub proxy to current connection.');
      this.priceManagementHub = $.connection.priceManagement;
      this.hubConnection = $.connection.hub;
    }
    return this.priceManagementHub;
  }

  // Publish state to observers
  private publishState(state: ConnectionState, reason?: string, attempt?: number) {
    this._connectionStateSubject.next({ state, since: Date.now(), attempt, reason });
  }

  // Simple client session id used across reconnects
  private getOrCreateSessionId(): string {
    try {
      const key = 'rpmsSessionId';
      let id = localStorage.getItem(key);
      if (!id) {
        id = this.generateId();
        localStorage.setItem(key, id);
      }
      return id;
    } catch {
      // Fallback (no localStorage)
      return this.generateId();
    }
  }

  // Telemetry hook: today logs to console; later can send to server (e.g., via navigator.sendBeacon)
  private logTelemetry(event: string, data?: any): void {
    if (this.config.logSignalR) console.log(`[SignalR][telemetry] ${event}`, data || '');
    // Example (disabled): navigator.sendBeacon(`${this.hubBaseUrl}/api/telemetry/signalr`, JSON.stringify({ event, data, ts: Date.now() }));
  }

  private stringifyErr(err: any): string {
    try {
      if (!err) return 'unknown';
      if (typeof err === 'string') return err;
      return JSON.stringify(err);
    } catch {
      return String(err);
    }
  }

  // Simple GUID-like generator
  private generateId(): string {
    // eslint-disable-next-line no-bitwise
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      // eslint-disable-next-line no-bitwise
      const r = Math.random() * 16 | 0;
      // eslint-disable-next-line no-bitwise
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
